import axios from 'axios'
import store from '../store'

// const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:3000/api/v1' : 'http://api.dpf-monitor.com/api/v1'
const baseURL = process.env.VUE_APP_API_ENDPOINT || 'http://localhost:3000/api/v1'

const api = axios.create({
  baseURL
})

api.interceptors.request.use(config => {
  const accessToken = localStorage.getItem('accessToken')
  if (accessToken) config.headers.authorization = accessToken

  return config
}, err => {
  console.error('request interceptor', err)
  throw Promise.reject(err)
})

api.interceptors.response.use(async function (response) {
  return response
}, async function (err) {
  if (err.response.status === 403 && err.response.data === 'jwt expired') {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) return Promise.reject(err)

    try {
      await store.dispatch('user/refreshAccessToken')
      const response = await api.request(err.response.config)
      return response
    } catch (err) {
      console.error(err)
      return Promise.reject(err)
    }
  } else {
    return Promise.reject(err)
  }
})

function throwHttpError (err) {
  if (err.response) {
    const error = new Error(err.response.data)
    err.status = err.response.status
    throw error
  } else {
    throw err
  }
}

export const signin = async (username, password) => {
  const body = { username, password }

  try {
    const res = await api.post('/signin', body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const signout = async () => {
  try {
    await api.post('/signout')
  } catch (err) {
    throwHttpError(err)
  }
}

export const getUserInfo = async () => {
  try {
    const res = await api.get('/users/info')
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const refreshAccessToken = async (refreshToken) => {
  try {
    const res = await api.post('/refresh', { refreshToken })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getPackets = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)
  const identityNo = options.identityNo

  const params = {
    limit, offset
  }

  if (identityNo) params.identityNo = identityNo

  console.log(params)

  try {
    const res = await api.get('/packets', {
      params
    })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getLastPacket = async (options = {}) => {
  const identityNo = options.identityNo

  const params = {}

  if (identityNo) params.identityNo = identityNo

  try {
    const res = await api.get('/packets/last', {
      params
    })

    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getPacketErrors = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/packets/errors', {
      params
    })

    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getDays = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/days', {
      params
    })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const downloadDay = async (id) => {
  try {
    const res = await api.get(`/days/${id}/download`)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const deleteDay = async (id) => {
  try {
    const res = await api.delete(`/days/${id}`)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getDevices = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)
  const deviceType = options.deviceType
  const modelNo = options.modelNo

  const params = {
    limit, offset
  }

  if (deviceType) params.deviceType = deviceType
  if (modelNo) params.modelNo = modelNo

  try {
    const res = await api.get('/devices', {
      params
    })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getDevice = async (id) => {
  try {
    const res = await api.get('/devices/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getFirmwares = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/firmwares', {
      params
    })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getFirmware = async (id) => {
  try {
    const res = await api.get('/firmwares/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const addFirmware = async (body) => {
  try {
    const res = await api.post('/firmwares', body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const editFirmware = async (id, body) => {
  try {
    const res = await api.put('/firmwares/' + id, body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const editDevice = async (id, options = {}) => {
  const { companyId, tags } = options

  const body = {
    companyId,
    tags
  }

  try {
    const res = await api.put('/devices/' + id, body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const setDeviceCompany = async (deviceId, companyId) => {
  try {
    const res = await api.put('/devices/' + deviceId + '/company', { companyId })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getTag = async (id) => {
  try {
    const res = await api.get('/tags/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const addTag = async (options = {}) => {
  const companyId = options.companyId
  const value = options.value

  try {
    const body = {
      companyId,
      value
    }

    const res = await api.post('/tags', body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const editTag = async (id, options = {}) => {
  const companyId = options.companyId
  const value = options.value

  try {
    const body = {
      companyId,
      value
    }

    const res = await api.put('/tags/' + id, body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getTags = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/tags', { params })
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const removeTag = async (id) => {
  try {
    const res = await api.delete('/tags/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getUsers = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/users', {
      params
    })

    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const removeUser = async (id) => {
  try {
    const res = await api.delete('/users/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const addUser = async (options = {}) => {
  const { username, password, companies } = options

  if (!username) throw new Error('Argument options.username must not be empty.')
  if (!password) throw new Error('Argument options.password must not be empty.')

  const body = {
    username,
    password,
    companies
  }

  try {
    const res = await api.post('/users', body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const editUser = async (id, options = {}) => {
  const { password, companies } = options

  const body = {
    companies
  }

  if (password) body.password = password

  try {
    const res = await api.put('/users/' + id, body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getUser = async (id) => {
  try {
    const res = await api.get('/users/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getCompanies = async (options = {}) => {
  const limit = parseInt(options.limit, 10)
  const offset = parseInt(options.offset, 10)

  const params = {
    limit, offset
  }

  try {
    const res = await api.get('/companies', {
      params
    })

    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const addCompany = async (options = {}) => {
  const name = options.name

  if (!name) throw new Error('Argument options.name must not be empty.')

  const body = {
    name
  }

  try {
    const res = await api.post('/companies', body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const getCompany = async (id) => {
  try {
    const res = await api.get('/companies/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const editCompany = async (id, options) => {
  const body = {
    name: options.name
  }

  try {
    const res = await api.put('/companies/' + id, body)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}

export const removeCompany = async (id) => {
  try {
    const res = await api.delete('/companies/' + id)
    return res.data
  } catch (err) {
    throwHttpError(err)
  }
}
